import { EvmChainId, type SushiSwapV3ChainId } from 'sushi/evm'
import type { PublicWagmiConfig } from '../../../config/public'
import type { ConcentratedLiquidityPosition } from '../types'
import { getConcentratedLiquidityPositionsFromTokenIds } from './getConcentratedLiquidityPositionsFromTokenIds'

// The SushiStaker contract only exists on Katana. Other chains — even if V3
// pools exist there — have no staked positions to query.
const STAKER_CHAIN_ID = EvmChainId.KATANA as SushiSwapV3ChainId

// The Katana API exposes staked V3 positions as a typed REST route that takes
// only the user address and returns the NFT token IDs as decimal strings.
const KATANA_API_URL =
  process.env['NEXT_PUBLIC_KATANA_API_URL']?.replace(/\/+$/, '') ||
  'https://api.katana.network'

interface StakedPositionsResponse {
  success: boolean
  data?: {
    token_ids?: string[]
  }
}

async function fetchStakedTokenIds(account: `0x${string}`): Promise<bigint[]> {
  const response = await fetch(
    `${KATANA_API_URL}/v1/dex-v3/users/${account.toLowerCase()}/staked-positions`,
    { headers: { Accept: 'application/json' } },
  )

  if (!response.ok) return []

  const result = (await response.json()) as StakedPositionsResponse
  if (!result.success) return []

  return (result.data?.token_ids ?? []).map((id) => BigInt(id))
}

export const getStakedConcentratedLiquidityPositions = async ({
  account,
  chainIds,
  config,
}: {
  account: `0x${string}` | undefined
  chainIds: readonly SushiSwapV3ChainId[]
  config: PublicWagmiConfig
}): Promise<ConcentratedLiquidityPosition[]> => {
  if (!account || !chainIds.includes(STAKER_CHAIN_ID)) return []

  try {
    const tokenIds = await fetchStakedTokenIds(account)
    if (tokenIds.length === 0) return []

    const positions = await getConcentratedLiquidityPositionsFromTokenIds({
      tokenIds: tokenIds.map((tokenId) => ({
        chainId: STAKER_CHAIN_ID,
        tokenId,
      })),
      config,
    })

    return positions.map((position) => ({ ...position, staked: true }))
  } catch {
    return []
  }
}
